import { useRef } from "react";
import { cn } from "@/lib/utils";
import { PendingHubEntityHandler } from "@/components/hub/pending-hub-entity-handler";
import { HubStoreProvider } from "@/components/hub/use-hub-store";
import { ActionStoreProvider, ActionWorkspace } from "./action";
import { AttentionWorkspace } from "./attention";
import { ColumnDivider } from "./column-divider";
import {
  NOTICE_COLLAPSE_BREAKPOINT,
  NOTICE_MAX,
  NOTICE_MIN,
  NOTICE_RAIL,
  previewColumnWidth,
  resolveColumnDrag,
} from "./column-layout";
import { MentorWorkflowsEmptyDashboard } from "./empty-dashboard";
import { NoticeSidebar } from "./notice/notice-sidebar";
import { SupportDetailsSidebar } from "./support-details-sidebar";
import { useNoticeSidebar } from "./notice/use-notice-sidebar";
import { useMentorWorkflowsSelection } from "./use-selection";
import {
  useViewportWidth,
  useWorkflowColumnLayout,
} from "./use-workflow-columns";

const noticeBounds = {
  rail: NOTICE_RAIL,
  min: NOTICE_MIN,
  max: NOTICE_MAX,
  expandAt: 150,
};

export function MentorWorkflowsShell() {
  const { selectedConversation, segment } = useMentorWorkflowsSelection();
  const setSearchQuery = useNoticeSidebar((state) => state.setSearchQuery);
  const viewport = useViewportWidth();
  const channelUrl = selectedConversation?.channel ?? "";
  const {
    noticeWidth,
    noticeUserCollapsed,
    noticePx,
    setNotice,
    dragging,
    setDragging,
    resetNotice,
  } = useWorkflowColumnLayout();
  const drag = useRef({
    width: noticeWidth,
    collapsed: noticeUserCollapsed,
    proposed: noticeWidth,
  });
  const noticeFrame = useRef<HTMLElement>(null);
  const noticeDivider = useRef<HTMLDivElement>(null);

  return (
    <main className="relative flex h-screen min-w-0 bg-surfaces-z3 text-text-primary">
      <section
        ref={noticeFrame}
        className={cn(
          "h-full shrink-0",
          !dragging && "transition-[width] duration-200 ease-out",
        )}
        style={{ width: noticePx }}
      >
        <NoticeSidebar />
      </section>
      <ColumnDivider
        label="Resize notice"
        nodeRef={noticeDivider}
        className={cn(
          "absolute bottom-0 top-0",
          !dragging && "transition-[left] duration-200 ease-out",
        )}
        style={{ left: noticePx - 4 }}
        onDragStart={() => {
          drag.current = {
            width: noticeUserCollapsed ? NOTICE_RAIL : noticeWidth,
            collapsed: noticeUserCollapsed,
            proposed: noticeUserCollapsed ? NOTICE_RAIL : noticeWidth,
          };
          setDragging(true);
        }}
        onDrag={(delta) => {
          if (viewport < NOTICE_COLLAPSE_BREAKPOINT) return;
          const proposed = drag.current.width + delta;
          drag.current.proposed = proposed;
          const preview = previewColumnWidth(
            proposed,
            drag.current.collapsed,
            noticeBounds,
          );
          if (noticeFrame.current) noticeFrame.current.style.width = `${preview}px`;
          if (noticeDivider.current) {
            noticeDivider.current.style.left = `${preview - 4}px`;
          }
        }}
        onDragEnd={() => {
          const autoCollapsed = viewport < NOTICE_COLLAPSE_BREAKPOINT;
          const next = autoCollapsed
            ? { collapsed: noticeUserCollapsed, width: noticeWidth }
            : resolveColumnDrag(
                drag.current.proposed,
                drag.current.collapsed,
                noticeWidth,
                noticeBounds,
              );
          if (!autoCollapsed) setNotice(next);
          if (next.collapsed) setSearchQuery("");
          const px =
            next.collapsed || autoCollapsed ? NOTICE_RAIL : next.width;
          if (noticeFrame.current) noticeFrame.current.style.width = `${px}px`;
          if (noticeDivider.current) {
            noticeDivider.current.style.left = `${px - 4}px`;
          }
          setDragging(false);
        }}
        onReset={resetNotice}
      />

      {selectedConversation ? (
        <div className="flex min-w-0 flex-1 items-stretch py-4 pl-2 pr-4">
          <div className="relative flex min-w-0 flex-1 overflow-hidden rounded-2xl bg-white shadow-[0_0_16px_rgb(0_0_0/0.08)]">
            {/* embedMode: create/view flows close back to Action tabs — never Hub list. */}
            <HubStoreProvider key={channelUrl} embedMode>
              <PendingHubEntityHandler channelUrl={selectedConversation.channel} />
              <ActionStoreProvider key={channelUrl}>
                <AttentionWorkspace />
                {segment === "support" ? (
                  <SupportDetailsSidebar
                    channelUrl={selectedConversation.channel}
                    internalChannelId={selectedConversation.internalChannelId}
                  />
                ) : (
                  <ActionWorkspace />
                )}
              </ActionStoreProvider>
            </HubStoreProvider>
          </div>
        </div>
      ) : (
        <MentorWorkflowsEmptyDashboard />
      )}
    </main>
  );
}
